import React from 'react';
import styles from './style.module.scss';
import ReviewHeader from '../common/ReviewHeader';
import TieredRate from './TieredRate';
import FlatRate from './FlatRate';
import ICPlus from './ICPlus';
import Surcharge from './Surcharge';
import DualPricing from './DualPricing';
import useSalesFormStore from '../../../../../../../../stores/sales-form';

/**
 * @typedef {Object} PricingValues
 * @property {string} pricing_model - The pricing model type
 * @property {number} [preferred_rate] - Preferred rate for dual pricing
 * @property {number} [monthly_fee] - Monthly fee for dual pricing
 * @property {number} [flat_rate] - Flat rate for flat rate pricing
 * @property {number} [ic_plus_rate] - IC+ rate for IC+ pricing
 * @property {number} [rate] - Rate for surcharge pricing
 * @property {number} [qualified_rate] - Qualified rate for tiered pricing
 */

/**
 * @typedef {Object} PricingProps
 * @property {Object} formik - Formik form instance
 * @property {Function} onEdit - Callback when edit button is clicked
 * @property {boolean} isOpen - Whether the section is expanded
 * @property {Function} onToggle - Callback when section is toggled
 */

/**
 * Pricing component for displaying different pricing models
 * @param {PricingProps} props - Component props
 * @returns {React.ReactElement} Pricing component
 */
const Pricing = ({ formik, onEdit, isOpen, onToggle }) => {
  const { values } = formik;
  const applicationData = useSalesFormStore(state => state.applicationData);

  /**
   * Formats a number as currency
   * @param {number} value - Number to format
   * @returns {string} Formatted currency string
   */
  const formatCurrency = (value) => {
    return value ? `$ ${parseFloat(value).toFixed(2)}` : '$ 0.00';
  };

  /**
   * Formats a number as percentage
   * @param {number} value - Number to format
   * @returns {string} Formatted percentage string
   */
  const formatPercentage = (value) => {
    return value ? `${parseFloat(value).toFixed(2)}%` : '0.00%';
  };

  const SectionHeader = ({ title }) => (
    <div className={styles.sectionHeader}>
      <span className={styles.sectionTitle}>{title}</span>
      <div className={styles.sectionLine} />
    </div>
  );

  const getPricingModelLabel = (model) => {
    const models = {
      dual_pricing: 'Dual Pricing',
      surcharge: 'Surcharge',
      flat_rate: 'Flat Rate',
      ic_plus: 'IC+',
      tiered: 'Tiered Rate'
    };
    return models[model] || model;
  };

  const renderPricingContent = () => {
    switch (applicationData.qualifiedtype) {
      case 'tiered':
        return <TieredRate values={applicationData.prices} applicationData={applicationData} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      case 'flat_rate':
        return <FlatRate values={values} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      case 'ic_plus':
        return <ICPlus values={values} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      case 'surcharge':
        return <Surcharge values={values} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      case 'dual_pricing':
        return <DualPricing values={applicationData.prices} formatCurrency={formatCurrency} formatPercentage={formatPercentage} />;
      default:
        return null;
    }
  };

  const getPricingDataById = (id) => {
    let pricing = applicationData.prices.find((price) => price.id === id);
    return pricing ? pricing.pivot : { dollars: 0, percent: 0 };
  };

  return (
    <div className={styles.pricing}>
      <ReviewHeader
        title="Pricing"
        isOpen={isOpen}
        onToggle={onToggle}
      />

      {isOpen && (
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Pricing Model</div>
                <div className={styles.detailValue}>{getPricingModelLabel(applicationData.qualifiedtype)}</div>
              </div>
            </div>
          </div>

          {renderPricingContent()}

          {/* Pin-Based Debit Section */}
          <div className={styles.section}>
            <SectionHeader title="PIN-BASED DEBIT" />
            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Network Fee</div>
                <div className={styles.detailValue}>{applicationData?.billing?.network_fee == 1 ? 'Yes' : 'No'}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Debit rate</div>
                <div className={styles.detailValue}>{formatPercentage(getPricingDataById(5).percent)}</div>
              </div>
              <div className={styles.detailItem}>
                <div className={styles.detailLabel}>Txn rate</div>
                <div className={styles.detailValue}>{formatCurrency(getPricingDataById(5).dollars)}</div>
              </div>
            </div>
          </div>

          {/* Default Fees Section */}
          <div className={styles.section}>
            <SectionHeader title="DEFAULT FEES" />
            <div className={styles.detailsGrid}>
              {[
                { key: 28, label: 'Setup Fee' },
                { key: 19, label: 'Monthly Access Fee' },
                { key: 42, label: '$100K Data Breach' },
                { key: 18, label: 'Monthly Minimum' },
                { key: 43, label: 'Semi-Annual Tech Fee' },
                { key: 12, label: 'Voice ARU' },
                { key: 14, label: 'Batch Fee' },
                { key: 22, label: 'Returned ACH Item' },
                { key: 13, label: 'AVS Fee' },
                { key: 11, label: '3rd Party Auth/Txn' },
                { key: 46, label: 'ETF Fee' },
                { key: 20, label: 'Chargeback Fee' },
                { key: 47, label: 'Representment Fee' },
                { key: 48, label: 'Pre-Dispute Fee' },
                { key: null, label: null }
              ].map((fee, index) => (
                <div key={index} className={styles.detailItem}>
                  {fee.label && fee.key && (
                    <>
                      <div className={styles.detailLabel}>{fee.label}</div>
                      <div className={styles.detailValue}>{formatCurrency(getPricingDataById(fee.key).dollars)}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* VAR Sheet Section */}
          {values.reprogram_hardware && values.reprogram_hardware.length > 0 && (
            <div className={styles.section}>
              <SectionHeader title="VAR SHEET" />
              <div className={styles.detailsGrid}>
                {values.reprogram_hardware.map((item, index) => (
                  <div key={index} className={styles.detailItem}>
                    <div className={styles.detailLabel}>Reprogram equipment {index + 1}</div>
                    <div className={styles.detailValue}>{item.model}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Pricing; 